/**
 * Expand single_select choices that were stored as comma-separated strings
 * (e.g. ["a, b", "c"] → ["A", "B", "C"]).
 */

export function toDisplayCase(value) {
  const text = String(value ?? '').trim();
  if (!text) return text;

  const stripped = text.replace(/-/g, '').replace(/,/g, '').replace(/\s/g, '');
  if (/\d/.test(text) && !/^[a-zA-Z]+$/.test(stripped)) {
    return text;
  }

  const titleWord = (word) => {
    if (!word) return word;
    if (/\d/.test(word)) return word;
    if (word.length === 1) return word.toUpperCase();
    return word[0].toUpperCase() + word.slice(1).toLowerCase();
  };

  if (text.includes('-')) {
    return text.split('-').map(titleWord).join('-');
  }
  if (text.includes(' ')) {
    return text.split(' ').map(titleWord).join(' ');
  }
  return titleWord(text);
}

function preferCanonicalChoice(existing, candidate) {
  if (existing.toLowerCase() !== candidate.toLowerCase()) return existing;
  return toDisplayCase(existing);
}

export function expandSelectChoices(choices) {
  if (!choices) return [];

  const items = Array.isArray(choices) ? choices : [choices];
  const seen = new Map();
  const expanded = [];

  for (const item of items) {
    if (item === null || item === undefined) continue;
    // Split on ", " only — avoids breaking values like "0-19,000" or "20,000-39,000".
    const parts = String(item).includes(', ')
      ? String(item).split(/,\s+/)
      : [String(item)];
    for (const part of parts) {
      const value = toDisplayCase(part.trim());
      if (!value) continue;
      const key = value.toLowerCase();
      if (seen.has(key)) {
        const canonical = preferCanonicalChoice(seen.get(key), value);
        seen.set(key, canonical);
        const idx = expanded.findIndex((entry) => entry.toLowerCase() === key);
        if (idx >= 0) expanded[idx] = canonical;
        continue;
      }
      seen.set(key, value);
      expanded.push(value);
    }
  }

  return expanded;
}

/**
 * Map a stored value to the matching schema choice (case-insensitive).
 */
export function canonicalizeSelectValue(value, choices) {
  if (value === null || value === undefined || value === '') return value;
  const normalizedChoices = expandSelectChoices(choices);
  if (!normalizedChoices.length) return value;

  const asString = String(value).trim();
  if (!asString) return value;

  const matched = normalizedChoices.find(
    (choice) => asString === String(choice) || asString.toLowerCase() === String(choice).toLowerCase(),
  );
  return matched !== undefined ? matched : value;
}

export function normalizeAttributeOptions(options) {
  if (!options || typeof options !== 'object') return options;
  if (!Array.isArray(options.choices)) return options;
  const choices = expandSelectChoices(options.choices);
  if (!choices.length) return options;
  return { ...options, choices };
}

export function normalizeSchemaAttributes(attributes = []) {
  return attributes.map((attr) => {
    if (!attr?.options?.choices) return attr;
    const options = normalizeAttributeOptions(attr.options);
    return options === attr.options ? attr : { ...attr, options };
  });
}

/**
 * Canonicalize single_select values in persona data before save.
 */
export function canonicalizePersonaSelectValues(schemaAttributes = [], personaData = {}) {
  if (!personaData || typeof personaData !== 'object') return personaData;

  const out = { ...personaData };
  for (const attr of schemaAttributes) {
    if (attr?.type !== 'single_select' || !attr.key) continue;
    const choices = expandSelectChoices(attr.options?.choices);
    if (!choices.length) continue;

    if (out[attr.key] !== undefined && out[attr.key] !== null && out[attr.key] !== '') {
      out[attr.key] = canonicalizeSelectValue(out[attr.key], choices);
    }

    for (const pcKey of ['personal_characteristics', 'personalCharacteristics']) {
      const pc = out[pcKey];
      if (!pc || typeof pc !== 'object' || pc[attr.key] === undefined) continue;
      if (pc[attr.key] === null || pc[attr.key] === '') continue;
      out[pcKey] = {
        ...pc,
        [attr.key]: canonicalizeSelectValue(pc[attr.key], choices),
      };
    }
  }

  return out;
}
